import { Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import {
  GET_REQUIREMENTS,
  INITIALIZE_DOCUMENTS,
  OwnedEntitiesRelationMap,
  REFRESH_REQUIREMENTS,
  REFRESH_REQUIREMENTS_DATA,
  RequirementsFilter,
  RequirementsOrdering,
  UPDATE_DIFFICULTY_LEVEL,
  UPSERT_DOC_REQUIREMENTS,
  UpsertDocRequirementInput,
} from './graphql/document-requirements';
import { Paginator, PaginatorInfo } from '../../types/paginator';
import OwnedEntity from '../../interfaces/owned-entity';
import DocType from '../../interfaces/doc-type';
import TaxReturnDifficultyLevel from '../../interfaces/tax-return-difficulty-levels';
import ID from '../../types/id';
import { DefaultSnackBarService } from '../../services/default-snack-bar.service';
import SqlOperator from '../../enums/sql-operator';
import { Aggregate, Direction } from '../../types/relation-order-by';
import DocGroupLog from "../../interfaces/doc-group-log";

const defaultOrder: RequirementsOrdering[] = [
  { column: 'NAME', order: Direction.ASC },
];
@Injectable({
  providedIn: 'root',
})
export class DocumentRequirementsService {
  private _docTypeRequirements: Paginator<OwnedEntity, PaginatorInfo> = {
    data: [],
    paginatorInfo: {
      perPage: 50,
      currentPage: 1,
      total: 0,
    },
  };
  private docTypeRequirements = new BehaviorSubject(this._docTypeRequirements);
  public docTypeRequirements$ = this.docTypeRequirements.asObservable();

  private _docTypes: DocType[] = [];
  private docTypes = new BehaviorSubject(this._docTypes);
  public docTypes$ = this.docTypes.asObservable();

  private _taxReturnDifficultyLevels: TaxReturnDifficultyLevel[] = [];
  private taxReturnDifficultyLevels = new BehaviorSubject(
    this._taxReturnDifficultyLevels
  );
  public taxReturnDifficultyLevels$ =
    this.taxReturnDifficultyLevels.asObservable();

  private _page = 1;
  private page = new BehaviorSubject(this._page);
  public page$ = this.page.asObservable();

  private _entityWhere: OwnedEntitiesRelationMap | null = null;
  private entityWhere = new BehaviorSubject(this._entityWhere);
  public entityWhere$ = this.entityWhere.asObservable();

  private _filter: RequirementsFilter = {};
  private filter = new BehaviorSubject(this._filter);
  public filter$ = this.filter.asObservable();

  private _includeAll: boolean = false;
  private includeAll = new BehaviorSubject(this._includeAll);
  public includeAll$ = this.includeAll.asObservable();

  private _order = defaultOrder;
  private order = new BehaviorSubject(this._order);
  private order$ = this.order.asObservable();

  private _newRowsRuns:DocGroupLog[] = [];
  private newRowsRuns = new BehaviorSubject(this._newRowsRuns);
  public newRowsRuns$ = this.newRowsRuns.asObservable();

  private _massUpdateRuns: DocGroupLog[] = [];
  private massUpdateRuns = new BehaviorSubject(this._massUpdateRuns);
  public massUpdateRuns$ = this.massUpdateRuns.asObservable();

  constructor(
    public apollo: Apollo,
    private _snackBar: DefaultSnackBarService
  ) {}

  private _setDocRequirements(
    docTypeRequirements: Paginator<OwnedEntity, PaginatorInfo>
  ) {
    this._docTypeRequirements = docTypeRequirements;
    this.docTypeRequirements.next(this._docTypeRequirements);
  }
  private _setDocTypes(docTypes: DocType[]) {
    this._docTypes = docTypes;
    this.docTypes.next(this._docTypes);
  }
  private _setMassUpdateRuns(massUpdateRuns: DocGroupLog[])
  {
    this._massUpdateRuns = massUpdateRuns;
    this.massUpdateRuns.next(this._massUpdateRuns);
  }
  private _setNewRowRuns(newRowsRuns: DocGroupLog[])
  {
    this._newRowsRuns = newRowsRuns;
    this.newRowsRuns.next(this._newRowsRuns);
  }
  private _setTaxReturnDifficultyLevels(
    taxReturnDifficultyLevels: TaxReturnDifficultyLevel[]
  ) {
    this._taxReturnDifficultyLevels = taxReturnDifficultyLevels;
    this.taxReturnDifficultyLevels.next(this._taxReturnDifficultyLevels);
  }
  public updateEntityWhere(name: string | null) {
    if (name?.length) {
      let wildcardName = `%${name}%`;
      this._entityWhere = {
        OR: [
          {
            column: 'OWNED_ENTITY_NAME',
            operator: SqlOperator.LIKE,
            value: wildcardName,
          },
          {
            column: 'RELATED_NAME',
            operator: SqlOperator.LIKE,
            value: wildcardName,
          },
        ],
      };
    } else {
      this._entityWhere = null;
    }
  }
  setFilter(input: RequirementsFilter) {
    this._filter = input;
    this.filter.next(this._filter);
  }
  setIncludeAll(includeAll: boolean) {
    this._includeAll = includeAll;
    this.includeAll.next(this._includeAll);
  }
  setOrder(order: string | null) {
    switch (order) {
      case 'name':
        this._order = [{ column: 'NAME', order: Direction.ASC }];
        break;
      case 'updated':
        this._order = [
          {
            documentTypeRequirements: {
              column: 'MODIFIED',
              aggregate: Aggregate.MAX,
            },
            order: Direction.ASC,
          },
        ];
        break;
      case 'difficulty':
        this._order = [
          {
            taxReturnDifficultyLevel: {
              column: 'DIFFICULTY_LEVEL',
              aggregate: Aggregate.MAX,
            },
            order: Direction.ASC,
          },
        ];
        break;
      default:
        this._order = defaultOrder;
    }
    this.order.next(this._order);
  }
  getDocTypeRequirements() {
    let sub: Subscription;
    sub = this.apollo
      .query({ query: GET_REQUIREMENTS, fetchPolicy: 'network-only' })
      .subscribe(({ data }) => {
        if (data?.ownedEntitiesPaged) {
          this._setDocRequirements(data.ownedEntitiesPaged);
        }
        if (data?.docTypes) {
          this._setDocTypes(data.docTypes);
        }
        if (data?.taxReturnDifficultyLevels) {
          this._setTaxReturnDifficultyLevels(data.taxReturnDifficultyLevels);
        }
        if(data?.newRowsRuns) {
          this._setNewRowRuns(data.newRowsRuns);
        }
        if(data?.massUpdateRuns) {
          this._setMassUpdateRuns(data.massUpdateRuns);
        }
        sub?.unsubscribe();
      });
  }
  public setPage(page: number) {
    this._page = page;
    this.page.next(this._page);
  }
  refreshDocTypeRequirements() {
    let sub: Subscription;
    sub = this.apollo
      .query({
        query: REFRESH_REQUIREMENTS,
        variables: {
          page: this._page,
          hasOwnedEntitiesMap: this._entityWhere,
          input: this._filter,
          hasTypeRequirements: this._includeAll
            ? { column: 'OWNED_ENTITY_ID', operator: SqlOperator.IS_NOT_NULL }
            : { column: 'REQUIREMENT_FLAG', operator: SqlOperator.IS_NOT_NULL },
          order: this._order,
        },
        fetchPolicy: 'network-only',
      })
      .subscribe(({ data }) => {
        if (data?.ownedEntitiesPaged) {
          this._setDocRequirements(data.ownedEntitiesPaged);
        }
        sub?.unsubscribe();
      });
  }
  initializeTracker(year: string) {
    return this.apollo.mutate({
      mutation: INITIALIZE_DOCUMENTS,
      variables: { year },
    });
  }
  refreshDocRequirements() {
    return this.apollo.mutate({ mutation: REFRESH_REQUIREMENTS_DATA });
  }
  upsertRequirements$(
    inputs: UpsertDocRequirementInput[] = []
  ): Observable<any> {
    return this.apollo.mutate({
      mutation: UPSERT_DOC_REQUIREMENTS,
      variables: { inputs },
    });
  }
  upsertDocRequirements(inputs: UpsertDocRequirementInput[] = []) {
    let sub: Subscription;
    sub = this.upsertRequirements$(inputs).subscribe(() => {
      this._snackBar.open("Saved Document Requirement data.")
      sub?.unsubscribe();
    });
  }
  updateDifficultyLevel(id: ID, tax_return_difficulty_level_id: ID | null) {
    let sub: Subscription;
    sub = this.apollo
      .mutate({
        mutation: UPDATE_DIFFICULTY_LEVEL,
        variables: { id, tax_return_difficulty_level_id },
      })
      .subscribe(() => {
        this._snackBar.open('Saved difficulty level');
      });
  }
}
